import {
    SpaceBetween,
    BreadcrumbGroup,
    Flashbar,
    Header,
    Container,
    Form,
    FormField,
    Input,
    Button,
    Multiselect,
} from '@amzn/awsui-components-console';
import React, { useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { fetchSubscribedGroups } from '../apis';
import { DDF_REGIONS } from '../../../lib/constants';
import InputWithValidation from '../common/InputWithValidation';
import { isValidInput } from '../../../lib/utils';
import { displayErrorContent } from '../../../lib/utils';
import { onboardBot } from '../apis';
import { CLIENT_ERROR, SERVER_ERROR } from '../../../lib/constants';

export default function OnboardBot() {
    const history = useHistory();
    const [errorMessage, setErrorMessage] = React.useState([]);
    const [username, setUsername] = React.useState('');
    const [usernameError, setUsernameError] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState('');
    const [description, setDescription] = React.useState('');
    const [ownerGroup, setOwnerGroup] = React.useState('');
    const [ownerGroupError, setOwnerGroupError] = React.useState('');
    const [groupsOptions, setGroupsOptions] = React.useState([]);
    const [groupsSelectedOptions, setGroupsSelectedOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [groupsStatus, setGroupsStatus] = React.useState('pending');

    /**
     * set initial status
     */
    const setInitialStatus = () => {
        setLoading(true);
        setErrorMessage([]);
        setUsernameError('');
        setPasswordError('');
        setOwnerGroupError('');
    };

    /**
     * validate input
     */
    const validateInput = () => {
        let errorflag = false;
        if (!isValidInput(username)) {
            setUsernameError('Username is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(password)) {
            setPasswordError('Password is required and should not be empty.');
            errorflag = true;
        }
        if (!isValidInput(ownerGroup)) {
            setOwnerGroupError('Owner group is required and should not be empty.');
            errorflag = true;
        }
        return !errorflag;
    };

    /**
     * click confirm button to trigger event
     */
    const onClick = async () => {
        if (groupsStatus === 'loading') {
            displayErrorContent(CLIENT_ERROR, 'Loading private groups, please wait.', setErrorMessage);
            return;
        }

        if (groupsStatus === 'error') {
            displayErrorContent(
                SERVER_ERROR,
                'Loading private groups failed, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                setErrorMessage
            );
            return;
        }

        let success = false;
        setInitialStatus();
        try {
            if (!validateInput()) {
                success = false;
            } else {
                let subscribedGroups = {};
                groupsSelectedOptions.forEach((group) => {
                    const [region, id] = group.value.split('_');
                    if (!subscribedGroups.hasOwnProperty(region)) {
                        subscribedGroups[region] = [];
                    }
                    subscribedGroups[region].push(id);
                });
                await onboardBot(username, password, description, ownerGroup, subscribedGroups);
                success = true;
            }
        } catch (err) {
            const errResponse = err.response;
            if (errResponse && errResponse.data && errResponse.status == 400) {
                const errorException = errResponse.data.errorException || 'Client Error';
                const errorMessage = errResponse.data.errorMessage || errResponse.data;
                displayErrorContent(errorException, errorMessage, setErrorMessage);
            } else {
                displayErrorContent(
                    SERVER_ERROR,
                    'Fail to onboard this bot account, try again later or contact our dev team @ds2-device-farm-dev for more details.',
                    setErrorMessage
                );
            }
        }
        setLoading(false);
        if (success) {
            history.push('/api-auth/bot-accounts');
        }
    };

    /**
     * set subscribed private groups options
     */
    const setSubscribedGroups = () => {
        (async function () {
            setGroupsStatus('loading');
            try {
                var options = [];
                for (let i = 0; i < DDF_REGIONS.length; i++) {
                    const region = DDF_REGIONS[i];
                    const regionName = region.regionName;
                    const response = await fetchSubscribedGroups(regionName);
                    const groups = response['response'];
                    options.push({
                        label: regionName,
                        options: groups
                            .filter((item) => item.privilege === 'admin')
                            .map((item) => ({
                                label: item.name,
                                value: `${regionName}_${item.id}`,
                                description: item.id,
                            })),
                    });
                }
                setGroupsOptions(options);
                setGroupsStatus('finished');
            } catch (e) {
                console.error('Fail to set selected private group(s) with error', e);
                setGroupsStatus('error');
            }
        })();
    };

    useEffect(() => {
        setSubscribedGroups();
    }, []);

    return (
        <SpaceBetween size="s">
            <BreadcrumbGroup
                items={[
                    { text: 'Amazon Device Farm', href: '#' },
                    { text: 'Bot accounts', href: '#/api-auth/bot-accounts' },
                    { text: 'Onboard', href: '#/api-auth/bot-accounts/onboard' },
                ]}
                ariaLabel="Breadcrumbs"
            />
            <form onSubmit={(e) => e.preventDefault()}>
                <Form
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            <Button formAction="none" href="#/api-auth/bot-accounts">
                                Back
                            </Button>
                            <Button formAction="none" loading={loading} onClick={onClick} variant="primary">
                                Confirm
                            </Button>
                        </SpaceBetween>
                    }
                >
                    <SpaceBetween direction="vertical" size="m">
                        <Container header={<Header variant="h2">Onboard bot account</Header>}>
                            <SpaceBetween direction="vertical" size="m">
                                <InputWithValidation
                                    label="Bot account username"
                                    type="text"
                                    description=""
                                    errorText={usernameError}
                                    placeholder="Enter bot account username"
                                    value={username}
                                    setValue={setUsername}
                                    setError={setUsernameError}
                                ></InputWithValidation>
                                <InputWithValidation
                                    label="Bot account password"
                                    type="password"
                                    description=""
                                    errorText={passwordError}
                                    placeholder="Enter bot account password"
                                    value={password}
                                    setValue={setPassword}
                                    setError={setPasswordError}
                                ></InputWithValidation>
                                <FormField
                                    label={
                                        <span>
                                            Description <i>- optional</i>{' '}
                                        </span>
                                    }
                                >
                                    <Input
                                        placeholder="Enter optional description"
                                        value={description}
                                        onChange={(event) => {
                                            setDescription(event.detail.value);
                                        }}
                                    />
                                </FormField>
                                <InputWithValidation
                                    label="Owner group"
                                    type="text"
                                    description="LDAP group that owns this bot account"
                                    errorText={ownerGroupError}
                                    placeholder="Enter owner group"
                                    value={ownerGroup}
                                    setValue={setOwnerGroup}
                                    setError={setOwnerGroupError}
                                ></InputWithValidation>
                                <FormField
                                    label={
                                        <span>
                                            Subscribed private groups <i>- optional</i>{' '}
                                        </span>
                                    }
                                    description="This step may take few seconds when multiple private groups are selected."
                                >
                                    <Multiselect
                                        selectedOptions={groupsSelectedOptions}
                                        expandToViewport
                                        placeholder="Select private groups to subscribe"
                                        loadingText="Loading groups"
                                        errorText="Error fetching groups"
                                        finishedText="End of all results"
                                        empty="No group found"
                                        filteringType="auto"
                                        virtualScroll={true}
                                        statusType={groupsStatus}
                                        options={groupsOptions}
                                        onChange={({ detail }) => {
                                            setGroupsSelectedOptions(detail.selectedOptions);
                                        }}
                                    />
                                </FormField>
                            </SpaceBetween>
                        </Container>
                        <Flashbar items={errorMessage} />
                    </SpaceBetween>
                </Form>
            </form>
        </SpaceBetween>
    );
}
